import { useTheme } from '@fluentui/react/lib/Theme';
import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPSpinner, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import {
  buildProjectNavModel,
  getProjectDeliverableCounts,
  getProjectNextMove,
  isProjectManager,
  ProjectNavMilestone,
} from 'components/Project/projectProgress';
import { getProjectRoute } from 'components/Project/projectRoutes';
import { getLastOpenedProjectId, getResumableProject, useActiveProjects } from 'components/Project/useActiveProjects';
import {
  getGlobalTextStylesLineClamp,
  globalFontBoldWeight,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Project, { ProjectSummary } from 'models/projects/project';
import { FormEvent, FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetProject } from 'services/Api/projectService';
import { apiRequest } from 'services/Auth/authConfig';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

// The ring beside the name and milestone, the next action under them, the button at the bottom.
// Config projectId 0 falls back to last-opened, then newest active.

// The donut reserves 40px of its height for a legend even when hidden, and FluentUIPieChart halves the
// width when the legend is hidden. 128/176 therefore render an 88px ring with a stroke of about 7px;
// the ring's box is cut to those 88px so the empty legend space takes no room in the tile.
const projectProgressRingHeight = 128;
const projectProgressRingWidth = 176;
const projectProgressRingSize = 88;

const ProjectProgressRing: FunctionComponent<{ done: number; total: number; label: string }> = (props) => {
  const { t } = useTranslation(['widgets']);
  const theme = useTheme();
  const done = Math.max(0, Math.min(props.total, props.done));
  const open = props.total - done;
  // An all-zero donut renders nothing; without deliverables show an empty ring and no tooltip.
  const isPlaceholder = props.total === 0;
  const data: IPieChartDataPoint[] = [
    { name: t('widgets:ProjectNextStep.Done'), value: done, color: theme.palette.themePrimary },
    {
      name: t('widgets:ProjectNextStep.Open'),
      value: isPlaceholder ? 1 : open,
      color: theme.semanticColors.bodyDivider,
    },
  ];

  return (
    <IPStack
      styles={{
        root: { width: projectProgressRingSize, height: projectProgressRingSize, flexShrink: 0, overflow: 'hidden' },
      }}
    >
      <FluentUIPieChart
        width={projectProgressRingWidth}
        height={projectProgressRingHeight}
        data={data}
        label={props.label}
        hideLegend
        hideTooltip={isPlaceholder}
      />
    </IPStack>
  );
};

export class WidgetProjectNextStepConfig {
  projectId: number;

  constructor() {
    this.projectId = 0;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.projectId = newRawConfig.projectId ?? 0;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetProjectNextStepConfig {
    const newConfig = new WidgetProjectNextStepConfig();
    newConfig.projectId = this.projectId;

    return newConfig;
  }
}

interface IWidgetProjectNextStepProps extends IWidgetRendererProps {}

const WidgetProjectNextStep: FunctionComponent<IWidgetProjectNextStepProps> = (props: IWidgetProjectNextStepProps) => {
  const { t } = useTranslation(['widgets', 'projects', 'translation']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const theme = useTheme();
  const { activeProjects, isLoading: isListLoading } = useActiveProjects();
  const [detail, setDetail] = useState<Project | undefined>(undefined);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);

  const config = new WidgetProjectNextStepConfig();
  config.load(props.widget.widgetConfig);

  const lastOpenedId = getLastOpenedProjectId(appContext);
  const configured =
    config.projectId > 0 ? activeProjects.find((project) => project.projectId === config.projectId) : undefined;
  const selected = configured ?? getResumableProject(activeProjects, lastOpenedId);

  useEffect(() => {
    let mounted = true;

    const loadDetail = async () => {
      if (selected === undefined) {
        setDetail(undefined);
        setIsDetailLoading(false);

        return;
      }

      try {
        setIsDetailLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const project = await apiGetProject(selected.projectId, accessToken, appContext.globalDataCache);
        if (!mounted) return;
        setDetail(project);
      } catch (err) {
        if (!mounted) return;
        appContext.setError(err);
        setDetail(undefined);
      } finally {
        if (mounted) setIsDetailLoading(false);
      }
    };

    void loadDetail();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.projectId, props.widget]);

  if (isListLoading || isDetailLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const project: Project | ProjectSummary | undefined = detail ?? selected;
  const milestones: ProjectNavMilestone[] =
    detail !== undefined && detail.hasDetail ? buildProjectNavModel(detail, t) : [];
  const move = getProjectNextMove(t, {
    project: project,
    milestones: milestones,
    isManager: isProjectManager(appContext),
  });
  // A move that opens a panel (the target date) has no route here; the project page offers it.
  const moveRoute: string | undefined =
    move.primary.route ?? (project === undefined ? undefined : getProjectRoute(project.projectId));

  if (project === undefined) {
    return (
      <IPStack
        verticalFill
        horizontalAlign="center"
        verticalAlign="center"
        tokens={globalStackTokensGapSmall}
        styles={{ root: { padding: 20 } }}
      >
        <IPStack.Item>
          <IPText block>{move.primary.detail ?? t('widgets:ProjectNextStep.NoProject')}</IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPPrimaryButton text={move.primary.buttonText} onClick={() => history.push(moveRoute ?? '/project')} />
        </IPStack.Item>
      </IPStack>
    );
  }

  const currentMilestone: ProjectNavMilestone | undefined =
    milestones.find((milestone) => milestone.state === 'current') ??
    milestones.find((milestone) => milestone.state !== 'done');
  const isProjectComplete = milestones.length > 0 && currentMilestone === undefined;
  const counts = detail !== undefined && detail.hasDetail ? getProjectDeliverableCounts(detail) : { done: 0, total: 0 };
  const percent = isProjectComplete ? 100 : counts.total === 0 ? 0 : Math.round((100 * counts.done) / counts.total);
  const milestoneIndex =
    currentMilestone === undefined
      ? 0
      : milestones.findIndex((milestone) => milestone.taskId === currentMilestone.taskId) + 1;
  const milestoneLine = isProjectComplete
    ? t('widgets:ProjectNextStep.Complete')
    : currentMilestone
    ? t('widgets:ProjectNextStep.Milestone', {
        name: currentMilestone.label,
        current: milestoneIndex,
        total: milestones.length,
      })
    : t('widgets:ProjectNextStep.NoMilestone');

  // The tile is a fixed 300px, so the ring sits beside the name and the milestone instead of under
  // them. Stacked, the cluster needed about 360px and pushed the button out of the card. The detail
  // keeps two lines; its full text is the tooltip.
  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { padding: '10px 6px 6px' } }}>
      <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapMedium}>
        <ProjectProgressRing
          done={isProjectComplete ? counts.total : counts.done}
          total={counts.total}
          label={t('widgets:ProjectNextStep.Status', { percent: percent })}
        />
        <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
          <IPText
            variant="mediumPlus"
            block
            title={project.name}
            styles={{ root: { fontWeight: globalFontBoldWeight, ...getGlobalTextStylesLineClamp(2) } }}
          >
            {project.name}
          </IPText>
          <IPText block styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
            {milestoneLine}
          </IPText>
        </IPStack.Item>
      </IPStack>
      <IPStack.Item grow styles={{ root: { minHeight: 0, overflow: 'hidden' } }}>
        {/* The action, as the project page names it; a fallback whose title is its button says it once. */}
        {move.primary.title !== move.primary.buttonText && (
          <IPText
            block
            title={move.primary.title}
            styles={{ root: { fontWeight: globalFontBoldWeight, ...getGlobalTextStylesLineClamp(2) } }}
          >
            {move.primary.title}
          </IPText>
        )}
        {move.primary.detail !== undefined && (
          <IPText block title={move.primary.detail} styles={{ root: getGlobalTextStylesLineClamp(2) }}>
            {move.primary.detail}
          </IPText>
        )}
      </IPStack.Item>
      <IPStack.Item>
        <IPPrimaryButton
          text={move.primary.buttonText}
          styles={{ root: { width: '100%' } }}
          onClick={() => history.push(moveRoute ?? '/project')}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetProjectNextStep;

interface IWidgetConfigProjectNextStepProps extends IWidgetConfigRendererProps {}

export const WidgetConfigProjectNextStep: FunctionComponent<IWidgetConfigProjectNextStepProps> = (
  props: IWidgetConfigProjectNextStepProps,
) => {
  const { t } = useTranslation(['widgets']);
  const { activeProjects, isLoading } = useActiveProjects();
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);
  const [projectId, setProjectId] = useState<number>(0);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      const config = new WidgetProjectNextStepConfig();
      config.load(props.widget.widgetConfig);
      setProjectId(config.projectId);
    }
  }, [currentWidgetId, props.widget]);

  const options: IPSelectableOption[] = [
    { key: 0, text: t('widgets:ProjectNextStep.Config.Automatic') },
    ...activeProjects.map((project) => ({
      key: project.projectId,
      text: project.name,
    })),
  ];

  const onChange = (_event: FormEvent<HTMLDivElement>, option?: IPSelectableOption) => {
    if (option === undefined) {
      return;
    }

    const next = new WidgetProjectNextStepConfig();
    next.projectId = option.key as number;
    setProjectId(next.projectId);
    props.onUpdateConfig(JSON.stringify(next), true);
  };

  return (
    <IPStack>
      <IPDropdown
        label={t('widgets:ProjectNextStep.Config.Project')}
        selectedKey={projectId}
        options={options}
        disabled={isLoading}
        onChange={onChange}
      />
    </IPStack>
  );
};
